import apiClient from './api';
import config from '../../config/environment';

class ComparisonService {
  constructor() {
    this.baseURL = config.API_BASE_URL;
  }

  // Get offers from a specific shop
  async getShopOffers(shopId, params = {}) {
    try {
      console.log(`Fetching offers for shop ${shopId} with params:`, params);
      
      let allOffers = [];
      let offset = 0;
      const limit = 100; // Batch size for each request
      let hasMoreOffers = true;
      
      // Fetch all offers using pagination
      while (hasMoreOffers) {
        const apiParams = {
          'publication.status': 'ACTIVE',
          limit,
          offset,
          ...params
        };
        
        const response = await apiClient.get(`${config.API_ENDPOINTS.ALLEGRO}/${shopId}/offers`, { 
          params: apiParams 
        });
        
        const responseData = response.data;
        const offers = responseData.offers || [];
        const totalCount = responseData.totalCount || responseData.count || 0;
        
        console.log(`Shop ${shopId}: Fetched ${offers.length} offers (offset: ${offset}, total: ${totalCount})`);
        
        if (offers.length === 0) {
          hasMoreOffers = false;
        } else {
          allOffers = [...allOffers, ...offers];
          offset += limit;
          
          // Check if we've fetched all offers
          if (allOffers.length >= totalCount) {
            hasMoreOffers = false;
          }
        }
      }
      
      console.log(`Shop ${shopId}: Total offers fetched: ${allOffers.length}`);
      return {
        offers: allOffers,
        count: allOffers.length,
        totalCount: allOffers.length
      };
    } catch (error) {
      console.error(`Error fetching shop offers for shop ${shopId}:`, error);
      throw error;
    }
  }

  // Get offers from multiple shops for comparison
  async getOffersForComparison(shopIds, params = {}) {
    try {
      const promises = shopIds.map(shopId => 
        this.getShopOffers(shopId, params).catch(error => {
          console.error(`Error fetching offers for shop ${shopId}:`, error);
          return { shopId, offers: [], error: error.message };
        })
      );
      
      const results = await Promise.all(promises);
      return results;
    } catch (error) {
      console.error('Error fetching offers for comparison:', error);
      throw error;
    }
  }

  // Compare offers by name/similarity
  async compareOffersByName(shopIds, searchTerm = '') {
    try {
      const params = searchTerm ? { name: searchTerm } : {};
      const results = await this.getOffersForComparison(shopIds, params);
      
      console.log('API Results:', results); // Debug log
      
      // Group offers by similar names for comparison
      const offerGroups = this.groupOffersByName(results);
      console.log('Offer Groups:', offerGroups); // Debug log
      return offerGroups;
    } catch (error) {
      console.error('Error comparing offers by name:', error);
      throw error;
    }
  }

  // Group offers by similar names using localeCompare
  groupOffersByName(shopResults) {
    const offerGroups = [];
    const processedOffers = new Set();
    
    shopResults.forEach(shopResult => {
      // Handle the API response structure: { offers: [...], count: 1, totalCount: 1 }
      const offers = shopResult.offers || [];
      
      offers.forEach(offer => {
        if (processedOffers.has(offer.id)) return;
        
        // Find similar offers across all shops
        const similarOffers = [];
        
        shopResults.forEach(otherShopResult => {
          const otherOffers = otherShopResult.offers || [];
          
          otherOffers.forEach(otherOffer => {
            if (processedOffers.has(otherOffer.id)) return;
            
            // Use localeCompare for string comparison
            const isSimilar = this.areOfferNamesSimilar(offer.name, otherOffer.name);
            
            if (isSimilar) {
              if (!similarOffers.find(o => o.id === otherOffer.id)) {
                similarOffers.push({
                  ...otherOffer,
                  shopId: otherShopResult.shopId
                });
              }
            }
          });
        });
        
        // If we found similar offers, create a group
        if (similarOffers.length > 1) {
          offerGroups.push({
            name: offer.name,
            offers: similarOffers
          });
          
          // Mark all offers in this group as processed
          similarOffers.forEach(o => processedOffers.add(o.id));
        }
      });
    });

    return offerGroups;
  }

  // Compare offer names using localeCompare
  areOfferNamesSimilar(name1, name2, locale = 'pl-PL') {
    if (!name1 || !name2) return false;
    
    // Use localeCompare with base sensitivity (ignore case and diacritics)
    const comparison = name1.localeCompare(name2, locale, {
      sensitivity: 'base',
      numeric: true
    });
    
    return comparison === 0;
  }

  // Get price comparison for similar offers
  getPriceComparison(offerGroup) {
    return offerGroup.offers.map(offer => ({
      shopId: offer.shopId,
      offerId: offer.id,
      name: offer.name,
      price: offer.sellingMode?.price?.amount,
      currency: offer.sellingMode?.price?.currency,
      stock: offer.stock?.available || 0,
      status: offer.publication?.status,
      image: offer.primaryImage?.url
    }));
  }
}

export default new ComparisonService(); 